import { UISimulation } from '@/components/help/UISimulation';

const STEP_DURATION = 2400;

// NavFrame shows the two-part left navigation: a slim, always-visible platform
// rail on the far left and the wider site workspace column (switcher + the
// site-scoped pages) to its right.
function SidebarFrame(): JSX.Element {
  const siteItems = ['Hub', 'Brainstorm', 'Pipeline', 'Drafts', 'Articles', 'Analytics'];
  const platformItems = ['Sites', 'Alerts', 'Templates', 'Setup', 'Settings', 'Help'];
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg overflow-hidden">
      <div className="grid grid-cols-[52px_150px_1fr]">
        {/* Far-left: slim, icon-only platform rail (always visible). */}
        <nav
          aria-label="Platform"
          className="bg-dash-surface border-r border-dash-border p-2 space-y-1"
        >
          <div className="text-[10px] font-mono font-semibold text-dash-primary text-center mb-2">
            AEO
          </div>
          {platformItems.map((it) => (
            <div
              key={it}
              className="rounded px-1 py-1 text-[8px] font-mono uppercase tracking-wide text-dash-secondary text-center border-l-2 border-transparent"
            >
              {it}
            </div>
          ))}
        </nav>
        {/* Workspace column: site switcher on top, then the site-scoped pages. */}
        <nav
          aria-label="Site"
          className="bg-dash-surface border-r border-dash-border p-3 space-y-3"
        >
          <div className="rounded border border-dash-border bg-dash-bg px-2 py-1.5">
            <div className="text-xs font-semibold text-dash-primary truncate">
              home-espresso
            </div>
            <div className="text-[9px] font-mono text-dash-muted">/home-espresso ▾</div>
          </div>
          <ul className="space-y-0.5">
            {siteItems.map((it, i) => (
              <li
                key={it}
                className={
                  i === 3
                    ? 'rounded px-2 py-1 text-xs text-accent bg-accent/10 border-l-2 border-accent'
                    : 'rounded px-2 py-1 text-xs text-dash-secondary border-l-2 border-transparent'
                }
              >
                {it}
              </li>
            ))}
          </ul>
        </nav>
        <div className="p-3 text-xs text-dash-secondary">
          <div className="text-dash-primary text-sm font-semibold mb-1">
            home-espresso · Drafts
          </div>
          <div>
            The platform rail is always there. The workspace column carries the
            site switcher and the active site&apos;s pages.
          </div>
        </div>
      </div>
    </div>
  );
}

// BreadcrumbFrame shows the persistent site switcher at the top of the
// workspace column and the dropdown it opens. (The header breadcrumb itself is
// now just plain context text.)
function BreadcrumbFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3 space-y-3">
      <div className="rounded border border-dash-border bg-dash-surface px-2 py-1.5 max-w-[220px]">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <div className="text-xs font-semibold text-dash-primary truncate">
              home-espresso
            </div>
            <div className="text-[9px] font-mono text-dash-muted">/home-espresso</div>
          </div>
          <span aria-hidden="true" className="text-dash-muted">
            ▾
          </span>
        </div>
      </div>
      <div className="rounded border border-dash-border bg-dash-surface shadow-lg p-3 max-w-[280px]">
        <div className="text-[10px] font-mono uppercase tracking-wide text-dash-muted mb-2">
          Switch site
        </div>
        <ul className="space-y-1">
          <li className="flex items-center justify-between rounded px-2 py-1.5 bg-accent/10 text-accent">
            <span className="text-xs">home-espresso</span>
            <span className="text-[10px] font-mono">active</span>
          </li>
          <li className="flex items-center justify-between rounded px-2 py-1.5 text-dash-secondary hover:bg-dash-bg">
            <span className="text-xs">naltrexone-guide</span>
            <span className="text-[10px] font-mono text-dash-muted">3 drafts</span>
          </li>
          <li className="flex items-center justify-between rounded px-2 py-1.5 text-dash-secondary hover:bg-dash-bg">
            <span className="text-xs">finance-101</span>
            <span className="text-[10px] font-mono text-dash-muted">live</span>
          </li>
        </ul>
      </div>
    </div>
  );
}

function CommandFrame(): JSX.Element {
  return (
    <div className="rounded-md border border-dash-border bg-dash-bg p-3">
      <div className="rounded border border-dash-border bg-dash-surface shadow-lg overflow-hidden">
        <div className="border-b border-dash-border px-3 py-2 flex items-center gap-2">
          <span className="text-[10px] font-mono text-dash-muted">⌘K</span>
          <input
            type="text"
            value="home"
            readOnly
            aria-label="Command palette query"
            className="flex-1 bg-transparent text-sm text-dash-primary placeholder:text-dash-muted focus:outline-none"
          />
        </div>
        <ul className="divide-y divide-dash-border">
          <li className="px-3 py-2 flex items-center justify-between bg-accent/10">
            <span className="text-xs text-accent">home-espresso</span>
            <span className="text-[10px] font-mono text-dash-muted">enter</span>
          </li>
          <li className="px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-dash-secondary">naltrexone-guide</span>
            <span className="text-[10px] font-mono text-dash-muted">↑↓</span>
          </li>
          <li className="px-3 py-2 flex items-center justify-between">
            <span className="text-xs text-dash-secondary">finance-101</span>
          </li>
        </ul>
      </div>
      <div className="mt-2 text-[11px] text-dash-muted text-center">
        ⌘K opens the switcher from anywhere.
      </div>
    </div>
  );
}

export function DashboardLayoutSimulation(): JSX.Element {
  return (
    <UISimulation
      steps={[
        {
          frame: <SidebarFrame />,
          label: 'Sidebar — Site rail + Platform rail',
          duration: STEP_DURATION,
        },
        {
          frame: <BreadcrumbFrame />,
          label: 'The site switcher sits atop the workspace',
          duration: STEP_DURATION,
        },
        {
          frame: <CommandFrame />,
          label: '⌘K opens the same switcher',
          duration: STEP_DURATION,
        },
      ]}
    />
  );
}
